/**
 * Color Swatches Module
 *
 * Handles color/pattern swatch selection with visual feedback.
 * Creates dynamic functions based on hook_name for backward compatibility.
 * Auto-initializes on elements with data-controller="color-swatches".
 *
 * Usage in Twig:
 * <div data-controller="color-swatches" data-swatches-hook="theme_colors" data-field-name="color">
 *     <label>
 *         <input type="radio" name="color" value="#FF0000" data-swatch-id="red">
 *         <div class="swatch-container-red"></div>
 *     </label>
 * </div>
 */

import { $, $$, on, ready } from '../utils/dom.js';

// Store for swatch managers by hook name
const swatchManagers = {};

/**
 * Initialize color swatches on a container
 * @param {Element} container - The swatches container element
 */
function initColorSwatches(container) {
    const hookName = container.dataset.swatchesHook || 'default';
    const fieldName = container.dataset.fieldName || 'color';

    /**
     * Select a swatch by ID
     * @param {string} swatchId - The swatch ID to select
     */
    function selectSwatch(swatchId) {
        const input = $(`input[data-swatch-id="${swatchId}"]`, container) ||
                      document.querySelector(`input[data-swatch-id="${swatchId}"]`);

        if (input) {
            input.checked = true;

            // Update all swatch styles - remove selection styling
            $$('[class*="swatch-container-"]', container).forEach(swatch => {
                swatch.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-500', 'border-blue-500');
                swatch.classList.add('border-gray-300');
            });

            // If not found in container, search globally
            if (container.querySelectorAll('[class*="swatch-container-"]').length === 0) {
                document.querySelectorAll('[class*="swatch-container-"]').forEach(swatch => {
                    swatch.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-500', 'border-blue-500');
                    swatch.classList.add('border-gray-300');
                });
            }

            // Highlight selected swatch
            const selectedSwatch = $(`.swatch-container-${swatchId}`, container) ||
                                   document.querySelector(`.swatch-container-${swatchId}`);
            if (selectedSwatch) {
                selectedSwatch.classList.remove('border-gray-300');
                selectedSwatch.classList.add('ring-2', 'ring-offset-2', 'ring-blue-500', 'border-blue-500');
            }

            // Dispatch custom event
            const event = new CustomEvent(`swatch_selected_${hookName}`, {
                detail: { swatchId }
            });
            document.dispatchEvent(event);

            // Also dispatch on container
            container.dispatchEvent(new CustomEvent('swatch:selected', {
                detail: { swatchId, hookName }
            }));
        }
    }

    /**
     * Get the currently selected swatch ID
     * @returns {string|null} The selected swatch ID
     */
    function getSelectedSwatch() {
        const checkedInput = $(`input[name="${fieldName}"]:checked`, container) ||
                            document.querySelector(`input[name="${fieldName}"]:checked`);
        return checkedInput?.dataset.swatchId || null;
    }

    // Click handler for swatch containers
    on(container, 'click', '[class*="swatch-container-"]', (e, swatch) => {
        // Extract swatch ID from class
        const match = swatch.className.match(/swatch-container-(\S+)/);
        if (match) {
            selectSwatch(match[1]);
        }
    });

    // Change handler for radio inputs
    on(container, 'change', 'input[type="radio"]', (e, input) => {
        if (input.dataset.swatchId) {
            selectSwatch(input.dataset.swatchId);
        }
    });

    // Initialize on load - highlight currently checked swatch
    const checkedInput = $(`input[name="${fieldName}"]:checked`, container) ||
                        document.querySelector(`input[name="${fieldName}"]:checked`);
    if (checkedInput && checkedInput.dataset.swatchId) {
        selectSwatch(checkedInput.dataset.swatchId);
    }

    // Store manager
    const manager = {
        selectSwatch,
        getSelectedSwatch,
        container
    };

    swatchManagers[hookName] = manager;
    container._colorSwatches = manager;

    // Create legacy global function
    createLegacyFunction(hookName);

    return manager;
}

/**
 * Create legacy global function for backward compatibility
 * @param {string} hookName - The hook name
 */
function createLegacyFunction(hookName) {
    const funcName = `selectSwatch_${hookName}`;

    if (!window[funcName]) {
        window[funcName] = function(swatchId) {
            // Try to find manager
            const manager = swatchManagers[hookName];
            if (manager) {
                manager.selectSwatch(swatchId);
            } else {
                // Fallback: direct DOM manipulation
                const input = document.querySelector(`input[data-swatch-id="${swatchId}"]`);
                if (input) {
                    input.checked = true;

                    // Update all swatch styles
                    document.querySelectorAll('[class*="swatch-container-"]').forEach(swatch => {
                        swatch.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-500', 'border-blue-500');
                        swatch.classList.add('border-gray-300');
                    });

                    // Highlight selected swatch
                    const selectedSwatch = document.querySelector(`.swatch-container-${swatchId}`);
                    if (selectedSwatch) {
                        selectedSwatch.classList.remove('border-gray-300');
                        selectedSwatch.classList.add('ring-2', 'ring-offset-2', 'ring-blue-500', 'border-blue-500');
                    }

                    // Dispatch custom event
                    const event = new CustomEvent(`swatch_selected_${hookName}`, {
                        detail: { swatchId }
                    });
                    document.dispatchEvent(event);
                }
            }
        };
    }
}

/**
 * Get a swatch manager by hook name
 * @param {string} hookName - The hook name
 * @returns {Object|null} The swatch manager
 */
export function getManager(hookName) {
    return swatchManagers[hookName] || null;
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    // Initialize data-controller="color-swatches" elements
    $$('[data-controller="color-swatches"]').forEach(initColorSwatches);

    // Find all swatch containers and create legacy functions for their hooks
    const hookNames = new Set();

    // Look for onclick handlers with selectSwatch_ pattern
    $$('[onclick*="selectSwatch_"]').forEach(el => {
        const match = el.getAttribute('onclick')?.match(/selectSwatch_(\w+)/);
        if (match && match[1]) {
            hookNames.add(match[1]);
        }
    });

    // Create legacy functions for discovered hooks
    hookNames.forEach(createLegacyFunction);

    // Always create default
    createLegacyFunction('default');
});

/**
 * Manual initialization
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        return initColorSwatches(element);
    }
}

export default {
    init,
    getManager
};
